import { MessagesSquare } from 'lucide-react';
import { useCallback } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useCanChat } from '../components/chat/ChatDock';
import { ChatView } from '../components/chat/ChatView';
import { InboxPanel } from '../components/chat/InboxPanel';
import { NewChat } from '../components/chat/NewChat';
import { AppFrame } from '../components/frame/AppFrame';
import { PageSpinner } from '../components/ui';
import { useSession } from '../lib/session';

/**
 * mogzo.com/messages (M38): the inbox and a chat side by side on a computer; on a phone, the
 * inbox (`/messages`, `/messages/requests`) or one chat (`/messages/<id>`) fills the screen.
 */
export function Messages() {
  const { me } = useSession();
  const canChat = useCanChat();
  const { id } = useParams();
  const { pathname, key } = useLocation();
  const navigate = useNavigate();
  const tab = pathname === '/messages/requests' ? 'requests' : 'chats';
  const starting = pathname === '/messages/new';
  const open = starting ? null : (id ?? null);
  const toInbox = useCallback(() => navigate('/messages'), [navigate]);

  if (me === undefined) return <PageSpinner />;
  if (me === null) return <Navigate to={`/sign-in?next=${encodeURIComponent(pathname)}`} replace />;
  if (!canChat) {
    return (
      <AppFrame title="Messages">
        <main className="mx-auto max-w-[520px] px-5 py-16 text-center">
          <h1 className="text-2xl font-bold">Messages</h1>
          <p className="mt-2 text-muted">Chat is for players 18 and over.</p>
        </main>
      </AppFrame>
    );
  }

  const showingChat = Boolean(open) || starting;
  return (
    <AppFrame fullHeight>
      <div className="flex h-dvh bg-card lg:h-[calc(100dvh-4rem)]">
        <div
          className={`min-h-0 w-full flex-col border-line lg:flex lg:w-[380px] lg:shrink-0 lg:border-r ${showingChat ? 'hidden' : 'flex'}`}
        >
          <InboxPanel
            variant="page"
            tab={tab}
            onTab={(next) => navigate(next === 'requests' ? '/messages/requests' : '/messages')}
            current={open}
            linkTo={(chat) => `/messages/${chat}`}
            onNew={() => navigate('/messages/new')}
            onBack={() => (key === 'default' ? navigate('/') : navigate(-1))}
          />
        </div>
        <div className={`min-h-0 min-w-0 flex-1 ${showingChat ? 'flex' : 'hidden lg:flex'}`}>
          {starting ? (
            <NewChat onBack={toInbox} onStarted={(chat) => navigate(`/messages/${chat}`)} />
          ) : open ? (
            <ChatView key={open} id={open} variant="page" onBack={toInbox} onGone={toInbox} />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              <span className="grid size-14 place-items-center rounded-full bg-field text-muted">
                <MessagesSquare size={24} strokeWidth={1.9} aria-hidden="true" />
              </span>
              <p className="text-lg font-semibold">Pick a chat</p>
              <p className="max-w-[320px] text-sm text-muted">
                Or start a new one with someone you’ve played with.
              </p>
              <button
                type="button"
                onClick={() => navigate('/messages/new')}
                className="mt-2 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-white"
              >
                New message
              </button>
            </div>
          )}
        </div>
      </div>
    </AppFrame>
  );
}
